import { defineComponent as _defineComponent } from 'vue'
import { type FormModel, message } from "ant-design-vue"
import { computed, nextTick, onMounted, ref, shallowRef } from "vue"
import { useRouter } from "vue-router/composables"

import i18n from "@/i18n"
import { fetchAttributesByType, fetchProductDraft } from "@/modules/offers/api/productModeration"
import type {
  FreeFormAttributeRowState,
  ProductDraftFormValues,
  ProductMainFieldsHandle
} from "@/modules/offers/types/createOffer"
import { ProductModerationAttribute, ProductModerationType } from "@/modules/offers/types/offers"
import type { ProductDraftResponse } from "@/modules/offers/types/productModeration"

import { loadTypeAttributes } from "../../utils/loadTypeAttributes"
import { hasAttributeValue, hasIncompleteFreeFormAttributes } from "../../utils/productAttributes"
import AttributeInputRow from "../ProductAttributes/AttributeInputRow.vue"
import AttributeSelectRow from "../ProductAttributes/AttributeSelectRow.vue"
import FreeFormAttributeRow from "../ProductAttributes/FreeFormAttributeRow.vue"

import ProductMainFields from "./ProductMainFields.vue"

type Emits = {
  (event: "change", product: ProductDraftFormValues): void
  (event: "ready", product: ProductDraftFormValues): void
  (event: "loadError"): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductDraftEditor',
  props: {
    initialProductName: { default: "" },
    showValidation: { type: Boolean }
  },
  emits: ["change", "ready", "loadError"],
  setup(__props: any, { expose, emit }: { emit: ({
  (event: "change", product: ProductDraftFormValues): void
  (event: "ready", product: ProductDraftFormValues): void
  (event: "loadError"): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { initialProductName: string, showValidation?: boolean };




const router = useRouter()

const formRef = ref<FormModel>()
const typeAttributes = ref<ProductModerationAttribute[]>([])
const freeFormRows = ref<FreeFormAttributeRowState[]>([])
const showFreeFormValidation = ref(false)

let nextFreeFormRowId = 0

const createFreeFormRow = (attribute: ProductModerationAttribute): FreeFormAttributeRowState => ({
  id: nextFreeFormRowId++,
  attribute
})
const getFreeFormAttributes = () => freeFormRows.value.map((row) => row.attribute)

const productDraft = ref<ProductDraftResponse>()

const loadProductDraft = async () => {
  try {
    const { data } = await fetchProductDraft({ oid: router.currentRoute.params.oid })
    productDraft.value = data
    selectedType.value = data.type || undefined
    await setAttributes(data.attributes)
    await nextTick()
    const values = getDraftValues()
    // Назва з пошуку ще не збережена: початковий стан беремо з відповіді API.
    emit("ready", {
      ...values,
      public_title_mlang: {
        uk: data.public_title_mlang?.uk || "",
        ru: data.public_title_mlang?.ru || ""
      }
    })
    emit("change", values)
  } catch {
    emit("loadError")
  }
}

const selectedType = ref<ProductModerationType>()

const selectedTypeName = computed<string>(() => selectedType.value?.full_name || "")

const isLoadingAttributes = ref(false)
const attributesLoadError = ref(false)

let typeLoadId = 0

const setProductType = async (type?: ProductModerationType) => {
  const loadId = ++typeLoadId
  attributesLoadError.value = false
  selectedType.value = type
  typeAttributes.value = []
  freeFormRows.value = []
  showFreeFormValidation.value = false
  emitAttributeChanges()

  if (!type) {
    isLoadingAttributes.value = false
    return
  }

  isLoadingAttributes.value = true
  try {
    const attributes = await loadTypeAttributes(type.code)

    if (loadId !== typeLoadId) return

    typeAttributes.value = attributes
    emitAttributeChanges()
  } catch {
    if (loadId === typeLoadId) attributesLoadError.value = true
  } finally {
    if (loadId === typeLoadId) isLoadingAttributes.value = false
  }
}

const isLoadingDraft = shallowRef<boolean>(true)

const isLoading = computed(
  () => isLoadingDraft.value || isLoadingAttributes.value || attributesLoadError.value
)

const setAttributes = async (serverAttributes: ProductDraftResponse["attributes"]) => {
  const attributes: ProductModerationAttribute[] = serverAttributes.map((attr) => ({
    ...attr,
    value_type: attr.value_type as ProductModerationAttribute["value_type"],
    value: [attr.value].flat().filter(Boolean)
  }))

  if (!selectedType.value) {
    freeFormRows.value = attributes.map(createFreeFormRow)
    typeAttributes.value = []
    isLoadingDraft.value = false
    return
  }

  const { data } = await fetchAttributesByType({
    code: selectedType.value.code
  })

  const typeAttributeCodes = new Set(data.results.map((item) => item.code))
  const attributesByCode = new Map(attributes.map((attr) => [attr.code, attr]))

  freeFormRows.value = attributes
    .filter((attr) => !typeAttributeCodes.has(attr.code))
    .map(createFreeFormRow)
  typeAttributes.value = data.results.map((item) => attributesByCode.get(item.code)).filter(Boolean)

  isLoadingDraft.value = false
}
const addFreeFormAttribute = () => {
  if (freeFormRows.value.length >= 20) return

  freeFormRows.value.push(createFreeFormRow({ value: [] }))
  emitAttributeChanges()
}
const getUnavailableAttributeCodes = (rowId: number) => [
  ...typeAttributes.value.map((attribute) => attribute.code),
  ...freeFormRows.value
    .filter((row) => row.id !== rowId)
    .map((row) => row.attribute.code)
    .filter(Boolean)
]
const updateFreeFormAttribute = (rowId: number, attribute: ProductModerationAttribute) => {
  if (attribute.code && getUnavailableAttributeCodes(rowId).includes(attribute.code)) {
    return message.error(`${i18n.t("offers.product_moderation_errors.attribute_already_added")}`)
  }

  const row = freeFormRows.value.find((item) => item.id === rowId)
  row.attribute = attribute
  emitAttributeChanges()
}
const removeFreeFormAttribute = (rowId: number) => {
  freeFormRows.value = freeFormRows.value.filter((row) => row.id !== rowId)
  emitAttributeChanges()
}
const updateAttribute = (attribute: ProductModerationAttribute) => {
  const index = typeAttributes.value.findIndex(({ code }) => code === attribute.code)
  typeAttributes.value.splice(index, 1, attribute)

  if (!isLoadingDraft.value) emitAttributeChanges()
}
const validateFreeFormAttributes = () => {
  showFreeFormValidation.value = true
  const isValid = !hasIncompleteFreeFormAttributes(getFreeFormAttributes())

  if (!isValid) {
    nextTick(() => {
      formRef.value.$el
        .querySelector('[data-incomplete-free-form="true"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" })
    })
  }

  return isValid
}
const emitAttributeChanges = () => {
  emit("change", {
    attributes: [...typeAttributes.value, ...getFreeFormAttributes()]
  })
}
const scrollToFirstError = async () => {
  await nextTick()
  formRef.value.$el
    .querySelector(".has-error")
    ?.scrollIntoView({ behavior: "smooth", block: "center" })
}

const mainFieldsRef = ref<ProductMainFieldsHandle>()

const getDraftValues = (): ProductDraftFormValues => ({
  ...mainFieldsRef.value?.getDraftValues(),
  attributes: [...typeAttributes.value, ...getFreeFormAttributes()]
})
const getFormValues = async () => {
  const payload = await mainFieldsRef.value?.getFormValues()

  if (!payload) return null

  return {
    ...payload,
    attributes: typeAttributes.value,
    free_form_attributes: getFreeFormAttributes()
  }
}
const resetForm = () => {
  productDraft.value = undefined
  mainFieldsRef.value?.resetForm()
  typeAttributes.value = []
  freeFormRows.value = []
}

onMounted(loadProductDraft)

expose({
  isLoading,
  resetForm,
  getFormValues,
  getDraftValues,
  scrollToFirstError,
  validateFreeFormAttributes
})

return { __sfc: true,props, emit, router, formRef, typeAttributes, freeFormRows, showFreeFormValidation, nextFreeFormRowId, createFreeFormRow, getFreeFormAttributes, productDraft, loadProductDraft, selectedType, selectedTypeName, isLoadingAttributes, attributesLoadError, typeLoadId, setProductType, isLoadingDraft, isLoading, setAttributes, addFreeFormAttribute, getUnavailableAttributeCodes, updateFreeFormAttribute, removeFreeFormAttribute, updateAttribute, validateFreeFormAttributes, emitAttributeChanges, scrollToFirstError, mainFieldsRef, getDraftValues, getFormValues, resetForm, hasAttributeValue, AttributeInputRow, AttributeSelectRow, FreeFormAttributeRow, ProductMainFields }
}

})